import { useState } from 'react';
import PropTypes from 'prop-types';

import { Tabs } from '@spotahome/ui-library';

import DropdownTabs from './DropdownTabs';

import './ChartContainer.scss';
import ToggleViewButton from './ToggleViewButton';

const ChartContainer = ({
  id,
  isMobile = false,
  title,
  subtitle,
  summary,
  chartAriaLabel,
  activeIndex = 0,
  onTabChange,
  renderChart,
  renderTable,
  sections = [],
  noscriptContent = ''
}) => {
  const [isViewTable, setIsViewTable] = useState(false);
  const titleId = `${id}-title`;

  const handleToggleView = () => {
    setIsViewTable(prev => !prev);
  };

  const renderChartAndTablePanels = () => (
    <>
      <div
        className={`chart-container__view chart-container__view--chart${
          isViewTable ? ' chart-container__view--hidden' : ''
        }`}
        aria-hidden={isViewTable}
      >
        <figure
          role="img"
          aria-label={chartAriaLabel || title}
          aria-describedby={`${id}-description`}
          className="chart-container__figure"
        >
          <figcaption className="chart-container__figure-caption">
            {title}
          </figcaption>
          {renderChart()}
        </figure>
      </div>
      <div
        className={`chart-container__view chart-container__view--table${
          !isViewTable ? ' chart-container__view--visually-hidden' : ''
        }`}
        aria-hidden={!isViewTable}
      >
        {renderTable()}
      </div>
    </>
  );

  const tabSections = sections.map(section => ({
    ...section,
    render: renderChartAndTablePanels
  }));

  return (
    <section id={id} className="chart-container" aria-labelledby={titleId}>
      <div className="chart-container__header">
        <h3 id={titleId} className="chart-container__header-title">{title}</h3>
        <ToggleViewButton
          className="chart-container__header-button"
          onClick={handleToggleView}
          isViewTable={isViewTable}
        />
      </div>
      <div className="chart-container__subtitle">
        <p>{subtitle}</p>
      </div>
      <div className="chart-container__chart">
        {sections.length > 0 &&
          (isMobile ? (
            <DropdownTabs
              className="chart-container__chart-tabs"
              items={tabSections}
              variant={'outlined'}
              onChange={onTabChange}
              activeIndex={activeIndex}
              handleOnClick={handleToggleView}
              isViewTable={isViewTable}
            />
          ) : (
            <Tabs
              className="chart-container__chart-tabs"
              tabClassName="chart-container__chart-tab"
              items={tabSections}
              variant={'outlined'}
              onChange={onTabChange}
              activeIndex={activeIndex}
            />
          ))}
        {sections.length === 0 && (
          <>
            <div className="chart-container__mobile-button-container">
              <ToggleViewButton
                className="chart-container__mobile-button"
                isViewTable={isViewTable}
                onClick={handleToggleView}
              />
            </div>
            {renderChartAndTablePanels()}
          </>
        )}
      </div>
      <p id={`${id}-description`} className="chart-container__description">{summary}</p>
      {noscriptContent && (
        // eslint-disable-next-line react/no-danger
        <noscript dangerouslySetInnerHTML={{ __html: noscriptContent }} />
      )}
    </section>
  );
};

export default ChartContainer;

ChartContainer.propTypes = {
  id: PropTypes.string.isRequired,
  isMobile: PropTypes.bool,
  location: PropTypes.shape({
    city: PropTypes.string,
    country: PropTypes.string
  }).isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  summary: PropTypes.string.isRequired,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  chartAriaLabel: PropTypes.string,
  activeIndex: PropTypes.number,
  noscriptContent: PropTypes.string,
  onTabChange: PropTypes.func.isRequired,
  renderChart: PropTypes.func.isRequired,
  renderTable: PropTypes.func.isRequired
};
